/**
 * Shared helpers for Infralytiqs SDK embed snippets — used by ShareReportsDialog
 * (create) and EditSavedReportDialog (edit saved shares).
 */
import React from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export type ShareTheme = 'PROFESSIONAL' | 'DEFAULT';
/** LIGHT/DARK = fixed. AUTOMATIC = follow host page theme at runtime (Option B). */
export type ShareThemeType = 'LIGHT' | 'DARK' | 'AUTOMATIC';

export interface ShareSuiteOption {
  _id: string;
  name: string;
  description?: string;
}

export interface EmbedSnippetConfig {
  apiBaseUrl: string;
  server: string;
  reportsuites: string[];
  theme: ShareTheme;
  themetype: ShareThemeType;
  colors?: Record<string, string>;
}

/**
 * Color-override fields exposed for the "Infralytiqs SDK" embed. Each maps to a
 * key consumed by the embed runtime (`InfralytiqsEmbedColors`) and forced onto
 * the rendered report suite. Grouped purely for display.
 */
export const COLOR_FIELD_GROUPS: { group: string; fields: { key: string; label: string }[] }[] = [
  {
    group: 'Backgrounds',
    fields: [
      { key: 'toolbarBg', label: 'Toolbar background' },
      { key: 'panelTitleBg', label: 'Panel title bar background' },
      { key: 'tableHeaderBg', label: 'Table header background' },
      { key: 'counterGradient', label: 'Counter tile accent (bottom-right)' },
    ],
  },
  {
    group: 'Text',
    fields: [
      { key: 'dropdownText', label: 'Dropdown label / value text' },
      { key: 'panelTitleText', label: 'Panel title bar text' },
      { key: 'tableHeaderText', label: 'Table header text' },
    ],
  },
  {
    group: 'Buttons',
    fields: [
      { key: 'buttonBg', label: 'Button background' },
      { key: 'buttonText', label: 'Button text' },
      { key: 'buttonBorder', label: 'Button border color' },
      { key: 'buttonActiveBg', label: 'Button background (hover/active/selected)' },
      { key: 'buttonActiveText', label: 'Button text (hover/active/selected)' },
    ],
  },
];

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** A HEX text input paired with a native color swatch; blank = no override. */
export const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({
  label, value, onChange,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: '1 1 230px', minWidth: 210 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="input"
        type="color"
        value={HEX6.test(value) ? value : '#000000'}
        onChange={(e) => onChange((e.target as HTMLInputElement).value)}
        aria-label={`${label} swatch`}
        sx={{
          width: 38, height: 38, p: 0, flexShrink: 0, cursor: 'pointer',
          border: '1px solid', borderColor: 'divider', borderRadius: 1, background: 'none',
        }}
      />
      <TextField
        size="small"
        placeholder="#RRGGBB"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ flex: 1 }}
        InputProps={{
          endAdornment: value ? (
            <IconButton size="small" onClick={() => onChange('')} aria-label={`Clear ${label}`}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined,
        }}
      />
    </Box>
  </Box>
);

export const buildCodeSnippet = (
  origin: string,
  apiBaseUrl: string,
  serverName: string,
  selectedSuiteNames: string[],
  themeParam: ShareTheme,
  themeType: ShareThemeType,
  colors: Record<string, string>,
): string => {
  const base = (apiBaseUrl || '').replace(/\/+$/, '');
  const cleanColors = Object.fromEntries(
    Object.entries(colors).filter(([, v]) => v && v.trim()),
  );
  const config: Record<string, unknown> = {
    apiBaseUrl: base,
    server: serverName,
    reportsuites: selectedSuiteNames,
    theme: themeParam,
    themetype: themeType,
  };
  if (Object.keys(cleanColors).length > 0) {
    config.colors = cleanColors;
  }
  const configJson = JSON.stringify(config, null, 2)
    .split('\n')
    .map((line, i) => (i === 0 ? line : `      ${line}`))
    .join('\n');

  // LIGHT/DARK: classic fixed snippet (unchanged behaviour).
  // AUTOMATIC: same loaders + render(); il-embed.js follows the host page theme
  // via InfralytiqsReports.update when data-tenant-admin-theme / .theme-* change.
  const automaticNote = themeType === 'AUTOMATIC'
    ? `
  // themetype AUTOMATIC: embed follows host light/dark (e.g. Tenant Admin header).
  // Requires il-embed.js that exposes InfralytiqsReports.update.`
    : '';

  return `<!-- Infralytiqs Report Suite embed -->
<div id="infralytiqs-report-suite"></div>
<script>
(function () {
  var ORIGIN = "${origin}";
  var CONFIG = ${configJson};${automaticNote}
  function mount() {
    if (!window.InfralytiqsReports || !window.InfralytiqsReports.render) {
      return setTimeout(mount, 50);
    }
    window.InfralytiqsReports.render(
      document.getElementById("infralytiqs-report-suite"),
      CONFIG
    );
  }
  var css = document.createElement("link");
  css.rel = "stylesheet";
  css.href = ORIGIN + "/static/il-embed.css";
  // Styles are injected at runtime by the widget; the stylesheet is optional.
  css.onerror = function () { css.parentNode && css.parentNode.removeChild(css); };
  document.head.appendChild(css);
  var js = document.createElement("script");
  js.src = ORIGIN + "/static/il-embed.js";
  js.async = true;
  js.onload = mount;
  document.head.appendChild(js);
})();
</script>`;
};

/** Parse the embedded CONFIG JSON from a saved Infralytiqs SDK snippet. */
export const parseSnippetConfig = (snippet: string): Partial<EmbedSnippetConfig> | null => {
  const match = snippet.match(/var CONFIG = (\{[\s\S]*?\});/);
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(match[1]) as Partial<EmbedSnippetConfig>;
    return parsed;
  } catch {
    return null;
  }
};

export const normalizeShareTheme = (value: unknown): ShareTheme =>
  (value === 'PROFESSIONAL' ? 'PROFESSIONAL' : 'DEFAULT');

export const normalizeShareThemeType = (value: unknown): ShareThemeType => {
  const v = String(value || '').toUpperCase();
  if (v === 'DARK') return 'DARK';
  if (v === 'AUTOMATIC' || v === 'AUTO' || v === 'HOST') return 'AUTOMATIC';
  return 'LIGHT';
};
